import { Card, CardContent, Stack, Typography } from '@mui/joy';
import { ArrowsDownUp, Plus } from '@phosphor-icons/react';
import { useState } from 'react';
import { useFieldArray, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getAttributeValueLabel } from 'companySettings/components/PartFinderAttributeValues';
import { AttributeStatus, AttributeType } from 'companySettings/enums';
import { IPartFinderProductType, IPartFinderProductTypeAttribute } from 'companySettings/types';
import { Button, Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { ISubmitAction } from 'core/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { AttributeCards } from './AttributeCards';
import { MIN_ATTRIBUTES_TO_ORDER } from './constants';
import { IOrderItem, OrderPanel } from './OrderPanel';
import { IProductTypeAttributeField, IUpsertPartFinderProductTypeFormData } from './types';
import { UpsertProductTypeAttribute } from './UpsertProductTypeAttribute';
import { hasProductTypeChanges } from './utils';
import { PartFinderProductTypeVersionHistory } from '../VersionHistory';

interface IAttributePanel {
  index?: number;
}

interface IProps {
  productType?: IPartFinderProductType;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IUpsertPartFinderProductTypeFormData>) => void;
  onSubmit: (
    status: AttributeStatus
  ) => (
    reset: UseFormReset<IUpsertPartFinderProductTypeFormData>
  ) => (data: IUpsertPartFinderProductTypeFormData) => Promise<void>;
}

export const UpsertPartFinderProductTypeFormFields = ({
  productType,
  isOpen,
  onClose,
  onSubmit,
}: IProps) => {
  // The attribute drawer: closed, adding (no index) or editing the attribute at an index.
  const [attributePanel, setAttributePanel] = useState<IAttributePanel | null>(null);
  const [orderingValuesIndex, setOrderingValuesIndex] = useState<number | null>(null);
  const [isAttributesOrderOpen, setIsAttributesOrderOpen] = useState(false);

  const { t } = useTranslation();

  const { control, watch, setError } = useFormContext();
  const { fields, append, update, remove, move, replace } = useFieldArray({
    control,
    name: 'attributes',
  });

  const [name, attributes = []]: [string, IPartFinderProductTypeAttribute[]] = watch([
    'name',
    'attributes',
  ]);
  // useFieldArray keys its rows by `id`, so the saved attribute ids are read from the form values.
  const attributeFields = fields as IProductTypeAttributeField[];

  const hasChanges = hasProductTypeChanges(name, attributes, productType);
  // A saved draft can be published as it is; a published type needs a change first.
  const hasUnpublishedDraft =
    Boolean(productType) && AttributeStatus.PUBLISHED !== productType.status;

  const editedAttribute =
    attributePanel?.index !== undefined ? attributes[attributePanel.index] : undefined;
  const existingNames = attributes
    .filter((_, index) => index !== attributePanel?.index)
    .map((attribute) => attribute.name);

  const orderingAttribute =
    null === orderingValuesIndex ? undefined : attributes[orderingValuesIndex];
  const valueOrderItems: IOrderItem[] = (orderingAttribute?.values ?? []).map((value, index) => ({
    id: String(index),
    label: getAttributeValueLabel(orderingAttribute.type, value, t),
    colorHex: AttributeType.COLOUR === orderingAttribute.type ? value.colorHex : undefined,
  }));
  const attributeOrderItems: IOrderItem[] = attributes.map((attribute, index) => ({
    id: String(index),
    label: attribute.name,
  }));

  const getSubmitActions = (): ISubmitAction[] => [
    {
      type: AttributeStatus.PUBLISHED,
      label: t('buttonLabelSaveAndPublish'),
      disabled: !hasChanges && !hasUnpublishedDraft,
      onSubmit: (reset) => (data: IUpsertPartFinderProductTypeFormData) => {
        if (!data?.attributes?.length) {
          setError('attributes', {
            type: 'min',
            message: t('errorPartFinderProductTypeAttributesRequired'),
          });

          return;
        }

        const emptyAttribute = data.attributes.find((attribute) => !attribute.values?.length);

        if (emptyAttribute) {
          setError('attributes', {
            type: 'min',
            message: t('errorPartFinderProductTypeAttributeValuesRequired', {
              name: emptyAttribute.name,
            }),
          });

          return;
        }

        return onSubmit(AttributeStatus.PUBLISHED)(reset)(data);
      },
    },
    {
      type: AttributeStatus.DRAFT,
      label: t('buttonLabelSaveAsDraft'),
      disabled: !hasChanges,
      // Enter saves a draft; publishing always takes the button.
      isDefault: true,
      onSubmit: onSubmit(AttributeStatus.DRAFT),
    },
  ];

  const onAttributeSaved = (attribute: IPartFinderProductTypeAttribute) => {
    if (attributePanel?.index !== undefined) {
      update(attributePanel.index, attribute);
    } else {
      append(attribute);
    }

    setAttributePanel(null);
  };

  const onRemoveValue = (index: number, valueIndex: number) => {
    const attribute = attributes[index];

    update(index, {
      ...attribute,
      values: attribute.values.filter((_, currentIndex) => currentIndex !== valueIndex),
    });
  };

  const onReorderValues = (orderedIds: string[]) => {
    if (!orderingAttribute) {
      return;
    }

    update(orderingValuesIndex, {
      ...orderingAttribute,
      values: orderedIds.map((id) => orderingAttribute.values[Number(id)]),
    });
  };

  const onReorderAttributes = (orderedIds: string[]) =>
    replace(orderedIds.map((id) => attributes[Number(id)]));

  return (
    <>
      <Form
        title={productType ? productType.name : t('titleCreatePartFinderProductType')}
        subtitle={
          productType ? (
            <PartFinderProductTypeVersionHistory productType={productType} />
          ) : undefined
        }
        isOpen={isOpen}
        onClose={onClose}
        getSubmitActions={getSubmitActions}
        cancelBtnLabel={t('buttonLabelCancel')}
        size={Size.LG}
      >
        <FormField
          name="name"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelName'), required: true }}
        />

        {/* The design shows the heading, hint and button once: inside the empty card while there
            are no attributes, and above the cards once there are. */}
        {attributeFields.length > 0 ? (
          <Stack sx={{ gap: 2 }}>
            <Stack
              direction={Direction.ROW}
              sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
            >
              <Stack sx={{ gap: 0.5 }}>
                <Typography level={Level.TITLE_MD}>{t('labelAttributes')}</Typography>
                <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                  {t('labelPartFinderProductTypeAttributesHint')}
                </Typography>
              </Stack>

              <Stack direction={Direction.ROW} sx={{ gap: 1.5, flexShrink: 0 }}>
                {attributes.length >= MIN_ATTRIBUTES_TO_ORDER && (
                  <Button
                    text={t('buttonLabelDefineAttributesOrder')}
                    variant={Variant.OUTLINED}
                    color={Color.NEUTRAL}
                    startDecorator={<ArrowsDownUp />}
                    onClick={() => setIsAttributesOrderOpen(true)}
                  />
                )}
                <Button
                  text={t('buttonLabelAddAttributes')}
                  color={Color.SUCCESS}
                  startDecorator={<Plus />}
                  onClick={() => setAttributePanel({})}
                />
              </Stack>
            </Stack>

            <AttributeCards
              attributes={attributeFields}
              onEdit={(index) => setAttributePanel({ index })}
              onRemove={remove}
              onMove={move}
              onDefineValuesOrder={setOrderingValuesIndex}
              onRemoveValue={onRemoveValue}
            />
          </Stack>
        ) : (
          <Card variant={Variant.OUTLINED}>
            <CardContent>
              <Stack sx={{ alignItems: 'center', gap: 1, py: 3 }}>
                <Typography level={Level.TITLE_MD}>{t('labelAttributes')}</Typography>
                <Typography
                  level={Level.TITLE_SM}
                  textAlign="center"
                  sx={{ color: 'neutral.600', maxWidth: 360 }}
                >
                  {t('labelPartFinderProductTypeAttributesHint')}
                </Typography>
                <Button
                  text={t('buttonLabelAddAttributes')}
                  color={Color.SUCCESS}
                  startDecorator={<Plus />}
                  onClick={() => setAttributePanel({})}
                />
              </Stack>
            </CardContent>
          </Card>
        )}
      </Form>

      <UpsertProductTypeAttribute
        attribute={editedAttribute}
        existingNames={existingNames}
        isOpen={Boolean(attributePanel)}
        onClose={() => setAttributePanel(null)}
        onSaved={onAttributeSaved}
      />

      <OrderPanel
        items={valueOrderItems}
        title={t('titleDefineValuesOrder')}
        subtitle={orderingAttribute?.name ?? ''}
        isOpen={null !== orderingValuesIndex}
        onClose={() => setOrderingValuesIndex(null)}
        onSave={onReorderValues}
      />

      <OrderPanel
        items={attributeOrderItems}
        title={t('titleDefineAttributesOrder')}
        subtitle={t('labelAttributes')}
        isOpen={isAttributesOrderOpen}
        onClose={() => setIsAttributesOrderOpen(false)}
        onSave={onReorderAttributes}
      />
    </>
  );
};
